import { useEffect, useState } from 'react';
import type { PersonKind } from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Dialog, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';

interface ChangeKindDialogProps {
  open: boolean;
  personId: string | null;
  currentKind: PersonKind | null;
  onClose: () => void;
  onConfirmed: () => void;
}

const KINDS: PersonKind[] = ['human', 'bot', 'service'];

export function ChangeKindDialog({
  open,
  personId,
  currentKind,
  onClose,
  onConfirmed,
}: Readonly<ChangeKindDialogProps>) {
  const [kind, setKind] = useState<PersonKind>(currentKind ?? 'human');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKind(currentKind ?? 'human');
      setError(null);
    }
  }, [open, currentKind]);

  const handleConfirm = async (): Promise<void> => {
    if (!personId) return;
    setSubmitting(true);
    setError(null);
    try {
      await personApi.updateKind(personId, { kind });
      onConfirmed();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to update kind'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Change kind">
      <fieldset className="space-y-2 text-sm">
        <legend className="sr-only">Choose a kind</legend>
        {KINDS.map((k) => (
          <label key={k} className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="person-kind"
              value={k}
              checked={kind === k}
              onChange={() => setKind(k)}
            />
            <span className="font-medium capitalize">{k}</span>
          </label>
        ))}
      </fieldset>

      {error && <p className="mt-3 text-sm text-error-700">{error}</p>}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} isLoading={submitting} disabled={submitting || kind === currentKind}>
          Save
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
